import { isNil } from "lodash";
import PropTypes from "prop-types";
import React, { Fragment } from "react";
import { connect } from "react-redux";

import FormButton from "../../components/form/FormButton";
import FormCheckbox from "../../components/form/FormCheckbox";
import SingleFormDatePicker from "../../components/form/formDatePicker/SingleFormDatePicker";
import ModalFormDropdown from "../../components/form/ModalFormDropdown";
import ModalField from "../../components/modal/ModalField";
import ModalGroup from "../../components/modal/ModalGroup";
import ModalRow from "../../components/modal/ModalRow";
import { AutoAdjustBackwardsLimitOptions } from "../../constants/autoAdjustBackwardsLimit";
import { AutoAdjustDateLevel, AutoAdjustDateOptions } from "../../constants/autoAdjustDateLevel";
import CalendarStyles from "../../constants/calendarStyles";
import TimeFormats from "../../constants/timeFormats";
import batchOperations from "../../modules/batchOperations";
import { calendarMarkerSelectors } from "../../modules/calendarMarkers";
import { definitionOperations } from "../../modules/definition";
import { calendarOperations } from "../../modules/definition/calendar";
import dateFunctions from "../../modules/definition/calendar/dateFunctions";
import { itemSelectors } from "../../modules/items";
import { preferencesSelectors } from "../../modules/preferences";
import { PropTypeShapes } from "../../types";
import calendarTypes from "../../types/calendarTypes";
import { mergeDispatchToPropsWithExtraProps, wrapEditingComponent } from "../../utils/codeSplit";
import { dateFormatOptions } from "../../utils/dateFormats";
import preprocessorQuery from "../../utils/preprocessorQuery";

const styleOptions = () => {
  return CalendarStyles.map(t => ({
    label: t.name,
    value: t.value
  }));
};

const timeFormatOptions = () => {
  return TimeFormats.map(t => ({
    label: t.name,
    value: t.identifier
  }));
};

const autoAdjustOptions = () => {
  return AutoAdjustDateOptions.map(o => ({
    label: o.name,
    value: o.identifier,
    description: o.description
  }));
};

const autoAdjustLimitOptions = () => {
  return AutoAdjustBackwardsLimitOptions.map(o => ({
    label: o.name,
    value: o.identifier,
    description: o.description
  }));
};

const SettingsDateGroup = props => {
  const isIos = preprocessorQuery.isIos();
  const disableEditDateFormat = false;
  const {
    calendar,
    overrideNowTimestamp,
    onEditCalendarClick,
    onDateFormatChange,
    onTimeFormatChange,
    onIncludeDayNameChange,
    onDefaultCenturyChange,
    autoAdjustLevel,
    autoAdjustBackwardsLimit,
    onChangeAutoAdjustLevel,
    onChangeAutoAdjustLimit,
    onChangeOverrideNowTimestamp,
    onWeekStartChange,
    canEditDateData,
    dateOrder
  } = props;
  const weekdayOptions = calendar.weekdays
    .map(wd => {
      return {
        label: wd.name,
        value: wd.name
      };
    })
    .toList()
    .toArray();

  const isDesktop = preprocessorQuery.isDesktop();

  let editingLabel = isDesktop ? "Editing" : null;

  const customizeShortDateBtn = (
    <FormButton onClick={props.onShowPreferencesModal} label="Customize short date" linkStyle={true} />
  );

  return (
    <Fragment>
      {wrapEditingComponent(
        <ModalGroup label="Calendar">
          <ModalRow minFieldWidth={true}>
            <ModalField label="Style" flex={6}>
              <ModalFormDropdown
                options={styleOptions()}
                value={calendar.calendarType}
                onChange={props.onCalendarTypeChange}
                title="Date Style"
                backText="Settings"
              />
            </ModalField>
            <ModalField label="Week starts on" flex={6}>
              <ModalFormDropdown
                disabled={
                  !canEditDateData ||
                  (calendar.calendarType != calendarTypes.CALENDAR_TYPE_DATE_TIME &&
                    calendar.calendarType != calendarTypes.CALENDAR_TYPE_WEEKDAY_TIME)
                }
                value={calendar.weekdays.get(0).name}
                options={weekdayOptions}
                onChange={val => onWeekStartChange(calendar, val)}
              />
            </ModalField>
            <ModalField showBlankLabel={!isIos} flex={6}>
              <FormButton label="Edit Calendar" onClick={onEditCalendarClick} />
            </ModalField>
          </ModalRow>
        </ModalGroup>
      )}
      <ModalGroup label="Formatting">
        <ModalRow minFieldWidth={true}>
          <ModalField label="Date Format" labelContent={isIos ? customizeShortDateBtn : null} flex={6}>
            <ModalFormDropdown
              disabled={disableEditDateFormat}
              options={dateFormatOptions(calendar.calendarType, dateOrder)}
              value={calendar.dateFormat}
              onChange={onDateFormatChange}
              title="Date Format"
              backText="Settings"
            />
          </ModalField>
          <ModalField label="Time Format" flex={6}>
            <ModalFormDropdown
              disabled={disableEditDateFormat}
              options={timeFormatOptions()}
              value={calendar.timeFormat}
              onChange={onTimeFormatChange}
              title="Time Format"
              backText="Settings"
            />
          </ModalField>
          <ModalField showBlankLabel={true} flex={6}>
            <FormCheckbox
              disabled={disableEditDateFormat}
              label="Include day name"
              checkboxState={calendar.dateFormatIncludesDayName}
              onChange={onIncludeDayNameChange}
            />
          </ModalField>
        </ModalRow>
        {!isIos ? <div id="shortDateLink">{customizeShortDateBtn}</div> : null}
        {wrapEditingComponent(
          <Fragment>
            <ModalRow>
              <ModalField label={editingLabel} flex={6}>
                <FormCheckbox
                  disabled={disableEditDateFormat}
                  label="Convert 2-digit years to current century"
                  checkboxState={calendar.defaultToModernCentury}
                  onChange={onDefaultCenturyChange}
                />
              </ModalField>
            </ModalRow>
          </Fragment>
        )}
      </ModalGroup>
      {wrapEditingComponent(
        <ModalGroup label="Automatic Calculations">
          <ModalRow>
            <ModalField label="Automatic Date Adjustment" flex={6}>
              <ModalFormDropdown
                options={autoAdjustOptions()}
                value={autoAdjustLevel}
                onChange={onChangeAutoAdjustLevel}
                title="Date Adjustment"
                backText="Settings"
                showDescription={true}
                alwaysPortal={!isDesktop}
              />
            </ModalField>
          </ModalRow>
          <ModalRow>
            <ModalField label="Adjustment Limits" flex={6}>
              <ModalFormDropdown
                options={autoAdjustLimitOptions()}
                value={autoAdjustBackwardsLimit}
                onChange={onChangeAutoAdjustLimit}
                title="Adjustment Limit"
                backText="Settings"
                showDescription={true}
                alwaysPortal={!isDesktop}
                disabled={Boolean(autoAdjustLevel != AutoAdjustDateLevel.ResolveConstraints)}
              />
            </ModalField>
          </ModalRow>
          <ModalRow minFieldWidth={true}>
            <ModalField>
              <FormCheckbox
                label="For ongoing item calculations, override current date/time with:"
                checkboxState={!isNil(overrideNowTimestamp)}
                onChange={value =>
                  onChangeOverrideNowTimestamp(value ? dateFunctions.timestampForToday(calendar) : null)
                }
              />
            </ModalField>
          </ModalRow>
          {!isNil(overrideNowTimestamp) ? (
            <ModalRow minFieldWidth={true}>
              <ModalField>
                <SingleFormDatePicker
                  value={dateFunctions.dateWithTimestamp(calendar, overrideNowTimestamp)}
                  calendar={calendar}
                  onChange={value => onChangeOverrideNowTimestamp(value.timestamp)}
                  disabled={isNil(overrideNowTimestamp)}
                />
              </ModalField>
            </ModalRow>
          ) : null}
        </ModalGroup>
      )}
    </Fragment>
  );
};

SettingsDateGroup.propTypes = {
  dispatchLocal: PropTypes.func.isRequired,
  onEditCalendarClick: PropTypes.func.isRequired,
  onCalendarTypeChange: PropTypes.func.isRequired,
  calendar: PropTypeShapes.calendar.isRequired,
  dateOrder: PropTypes.string.isRequired,
  overrideNowTimestamp: PropTypes.number,
  autoAdjustLevel: PropTypes.number,
  autoAdjustBackwardsLimit: PropTypes.string,
  onChangeAutoAdjustLevel: PropTypes.func.isRequired,
  onChangeAutoAdjustLimit: PropTypes.func.isRequired,
  onDateFormatChange: PropTypes.func.isRequired,
  onTimeFormatChange: PropTypes.func.isRequired,
  onIncludeDayNameChange: PropTypes.func.isRequired,
  onDefaultCenturyChange: PropTypes.func.isRequired,
  onChangeOverrideNowTimestamp: PropTypes.func.isRequired,
  onWeekStartChange: PropTypes.func.isRequired,
  canEditDateData: PropTypes.bool.isRequired,
  onShowPreferencesModal: PropTypes.func.isRequired
};

const getReadonlyActions = props => ({
  onDateFormatChange: format => {
    props.dispatchLocal(calendarOperations.changeDateFormat(format));
  },
  onTimeFormatChange: format => {
    props.dispatchLocal(calendarOperations.changeTimeFormat(format));
  },
  onIncludeDayNameChange: includeDay => {
    props.dispatchLocal(calendarOperations.changeDateFormatIncludesDayName(includeDay));
  }
});

const getEditingActions = props => ({
  onDefaultCenturyChange: defaultToModern => {
    props.dispatchLocal(calendarOperations.changeDefaultToModernCentury(defaultToModern));
  },
  onChangeAutoAdjustLevel: level => {
    props.dispatchLocal(definitionOperations.setAutoAdjustLevel(level));
  },
  onChangeAutoAdjustLimit: limit => {
    props.dispatchLocal(definitionOperations.setAutoAdjustBackwardsLimit(limit));
  },
  onWeekStartChange: (calendar, value) => {
    props.dispatchLocal(batchOperations.changeWeekStart(calendar, value));
  },
  onChangeOverrideNowTimestamp: timestamp => {
    props.dispatchLocal(definitionOperations.setOverrideNowTimestamp(timestamp));
  }
});

const mapDispatchToProps = (dispatch, props) => {
  return mergeDispatchToPropsWithExtraProps(getReadonlyActions, getEditingActions, props, dispatch);
};

const mapStateToProps = state => ({
  canEditDateData:
    itemSelectors.getItemsWithSetDates(state.present).count() === 0 &&
    calendarMarkerSelectors.getCalendarMarkersList(state.present).count() === 0,
  dateOrder: preferencesSelectors.getDateOrder(state.present)
});

export default connect(mapStateToProps, mapDispatchToProps)(SettingsDateGroup);
